import {
  Children,
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useId,
  useMemo,
} from 'react';

import { useFocusRing } from '@components/primitives';
import { Checkbox } from '@components/primitives/Checkbox';

import css from './styles.module.css';

interface CheckboxGroupContextType<T extends string> {
  value: T[];
  onChange?: (value: T[]) => void;
}

const CheckboxGroupContext = createContext<CheckboxGroupContextType<any>>({
  value: [],
  onChange: () => {},
});

export interface CheckboxItemProps {
  value: string;
  children: ReactNode;
}

/**
 * One option in the group, rendered as a selectable card. Compose its contents
 * with `Checkbox.Label` and `Checkbox.Icon`, the same parts a standalone
 * checkbox uses — the card styling is the only difference.
 */
function CheckboxItem({ children, value }: CheckboxItemProps) {
  const id = useId();
  const context = useContext(CheckboxGroupContext);

  const isChecked = useMemo(
    () => context.value.includes(value),
    [context.value, value]
  );

  const handleValueChange = useCallback(
    (checked: boolean) => {
      if (checked) {
        context.onChange?.([...context.value, value]);
      } else {
        context.onChange?.(context.value.filter((v) => v !== value));
      }
    },
    [context.onChange, context.value, value]
  );

  const [controlRef, focusStyle] = useFocusRing<HTMLButtonElement>();

  return (
    <div className={css.groupItem} style={focusStyle}>
      <Checkbox
        ref={controlRef}
        id={id}
        checked={isChecked}
        onChange={handleValueChange}
      >
        {children}
      </Checkbox>
    </div>
  );
}

export interface CheckboxGroupProps<T extends string> {
  children: ReactNode;
  value: T[];
  onChange?: (value: T[]) => void;
  columns?: number;
}

function CheckboxGroup<T extends string>({
  children,
  value,
  onChange,
  columns: manualColumns,
}: CheckboxGroupProps<T>) {
  const count = Children.count(children);
  const columns = useMemo(() => {
    if (manualColumns) return manualColumns;

    if (count <= 1) return 1;
    if (count % 3 === 0) return 3;
    return 2;
  }, [count, manualColumns]);

  return (
    <CheckboxGroupContext.Provider value={{ value, onChange }}>
      <div className={css.group} style={{ '--columns': columns }}>
        {children}
      </div>
    </CheckboxGroupContext.Provider>
  );
}

const CheckboxGroupNamespace = Object.assign(CheckboxGroup, {
  Item: CheckboxItem,
});

export { CheckboxGroupNamespace as CheckboxGroup };
